import React, { useEffect, useMemo, useState } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Quote, quotesAPI } from '../../services/api';
import { Button, Modal } from '../ui';
import ClientRecipientSelector, {
  getDefaultClientRecipients,
} from '../emails/ClientRecipientSelector';

interface QuoteSendModalProps {
  quote: Quote | null;
  onClose: () => void;
  onSent: () => void | Promise<void>;
}

const QuoteSendModal: React.FC<QuoteSendModalProps> = ({ quote, onClose, onSent }) => {
  const client = useMemo(() => quote?.client || quote?.deal?.client, [quote]);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setRecipients(getDefaultClientRecipients(client));
    setError('');
  }, [client, quote?.id]);

  const handleSend = async () => {
    if (!quote || recipients.length === 0) {
      setError('Select at least one recipient');
      return;
    }

    try {
      setSending(true);
      setError('');
      await quotesAPI.send(quote.id, { to: recipients });
      await onSent();
      onClose();
    } catch (sendError: any) {
      setError(sendError.response?.data?.message || 'Failed to send quote');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={Boolean(quote)}
      onClose={onClose}
      title="Send Quote"
      subtitle={quote ? `${quote.quoteNumber}${client?.name ? ` to ${client.name}` : ''}` : undefined}
      size="md"
    >
      <div className="space-y-5">
        <ClientRecipientSelector client={client} value={recipients} onChange={setRecipients} />
        {error && <p className="text-sm text-error-600">{error}</p>}
        <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSend}
            isLoading={sending}
            disabled={recipients.length === 0}
            leftIcon={<PaperAirplaneIcon className="h-4 w-4" />}
          >
            Send Quote
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QuoteSendModal;
